import { useState, useEffect } from "react";
import axios from "axios";
import { ExternalLink, ChevronDown, Building2, Phone, ArrowLeft } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const COLOR_MAP = {
  cyan:   { tag: "#22d3ee", glow: "rgba(34,211,238,0.18)" },
  purple: { tag: "#c084fc", glow: "rgba(192,132,252,0.18)" },
  green:  { tag: "#4ade80", glow: "rgba(74,222,128,0.18)" },
  pink:   { tag: "#f472b6", glow: "rgba(244,114,182,0.18)" },
  orange: { tag: "#fb923c", glow: "rgba(251,146,60,0.18)" },
  blue:   { tag: "#60a5fa", glow: "rgba(96,165,250,0.18)" },
  yellow: { tag: "#facc15", glow: "rgba(250,204,21,0.18)" },
};

export default function PublicCreditBuilderListPage() {
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openIds, setOpenIds] = useState({});

  useEffect(() => {
    axios.get(`${API}/api/credit-builder-config`)
      .then(r => {
        const sec = r.data?.sections || [];
        setSections(sec);
        // open the first section by default
        if (sec.length) setOpenIds({ [sec[0].id]: true });
      })
      .catch(() => setSections([]))
      .finally(() => setLoading(false));
  }, []);

  const toggle = (id) => setOpenIds(prev => ({ ...prev, [id]: !prev[id] }));

  return (
    <div data-testid="public-credit-builder-page" style={styles.page}>
      <div style={styles.gridBg} />
      <div style={{ ...styles.orb, ...styles.orb1 }} />
      <div style={{ ...styles.orb, ...styles.orb2 }} />

      <header style={styles.header}>
        <a href="/" style={styles.brand} data-testid="back-home-link">
          <div style={styles.brandMark}>C</div>
          <span>Clear All Credit</span>
        </a>
        <a href="tel:6198820259" style={styles.callBtn} data-testid="call-btn">
          <Phone size={14} /> (619) 882-0259
        </a>
      </header>

      <main style={styles.main}>
        <a href="https://www.clearallcreditrepairs.com" style={styles.backLink}>
          <ArrowLeft size={16} /> Back to website
        </a>

        <div style={styles.tag}>FREE RESOURCE</div>
        <h1 style={styles.h1}>The Credit Builder <span style={styles.gold}>Master List</span></h1>
        <p style={styles.lead}>
          Every product, platform, and program our team uses to help clients rebuild credit fast — vetted, organized, and ready to use today. No fluff. No paid placements.
        </p>

        {loading && <div style={styles.loading}>Loading the master list…</div>}

        {!loading && sections.map(section => {
          const c = COLOR_MAP[section.color] || COLOR_MAP.cyan;
          const open = !!openIds[section.id];
          return (
            <section key={section.id} style={styles.section} data-testid={`section-${section.id}`}>
              <button
                onClick={() => toggle(section.id)}
                style={{ ...styles.sectionBtn, borderColor: open ? c.tag : "rgba(255,255,255,0.08)", boxShadow: open ? `0 0 40px ${c.glow}` : "none" }}
                data-testid={`toggle-${section.id}`}
              >
                <div style={styles.sectionTitle}>
                  <span style={{ ...styles.sectionTag, color: c.tag, borderColor: c.tag, background: c.glow }}>
                    {section.icon}
                  </span>
                  <span>{section.title}</span>
                  <span style={styles.count}>{section.items?.length || 0}</span>
                </div>
                <ChevronDown size={20} style={{ transform: open ? "rotate(180deg)" : "none", transition: "transform 0.3s", color: c.tag }} />
              </button>

              {open && (
                <div style={styles.itemsGrid}>
                  {(section.items || []).map(item => (
                    <a
                      key={item.id}
                      href={item.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ ...styles.itemCard, borderColor: "rgba(255,255,255,0.08)" }}
                      onMouseEnter={(e) => { e.currentTarget.style.borderColor = c.tag; e.currentTarget.style.transform = "translateY(-4px)"; }}
                      onMouseLeave={(e) => { e.currentTarget.style.borderColor = "rgba(255,255,255,0.08)"; e.currentTarget.style.transform = "translateY(0)"; }}
                      data-testid={`item-${item.id}`}
                    >
                      <div style={styles.itemHead}>
                        <div style={styles.itemIcon}><Building2 size={20} color={c.tag} /></div>
                        <div style={styles.itemName}>{item.name}</div>
                        <ExternalLink size={14} style={{ opacity: 0.5 }} />
                      </div>
                      <p style={styles.itemDesc}>{item.desc}</p>
                    </a>
                  ))}
                </div>
              )}
            </section>
          );
        })}

        <div style={styles.cta}>
          <h2 style={styles.ctaTitle}>Need help applying any of these?</h2>
          <p style={styles.ctaText}>Schedule a free 15-minute consultation and we'll personally walk you through the right products for YOUR credit profile.</p>
          <div style={styles.ctaBtns}>
            <a href="tel:6198820259" style={styles.btnPrimary} data-testid="cta-call">
              <Phone size={18} /> Call (619) 882-0259
            </a>
            <a href="https://pdf-gen-preview-1.emergent.host/client-intake" target="_blank" rel="noopener noreferrer" style={styles.btnSecondary} data-testid="cta-intake">
              Start Free Intake →
            </a>
          </div>
        </div>
      </main>

      <footer style={styles.footer}>
        © 2026 Clear All Credit Repairs · CROA Compliant · San Diego, CA
      </footer>
    </div>
  );
}

const styles = {
  page: { minHeight: "100vh", background: "#050816", color: "#e7ecff", fontFamily: "'Inter', sans-serif", position: "relative", overflowX: "hidden" },
  gridBg: { position: "fixed", inset: 0, backgroundImage: "linear-gradient(rgba(111,139,255,0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(111,139,255,0.05) 1px, transparent 1px)", backgroundSize: "60px 60px", maskImage: "radial-gradient(ellipse at center, black 30%, transparent 75%)", pointerEvents: "none" },
  orb: { position: "fixed", borderRadius: "50%", filter: "blur(120px)", pointerEvents: "none", zIndex: 0 },
  orb1: { width: 500, height: 500, background: "#6f8bff", top: -180, left: -180, opacity: 0.3 },
  orb2: { width: 500, height: 500, background: "#ffc857", bottom: -180, right: -180, opacity: 0.2 },
  header: { position: "sticky", top: 0, zIndex: 50, display: "flex", justifyContent: "space-between", alignItems: "center", padding: "16px 24px", background: "rgba(5,8,22,0.7)", backdropFilter: "blur(20px)", WebkitBackdropFilter: "blur(20px)", borderBottom: "1px solid rgba(255,255,255,0.08)" },
  brand: { display: "flex", alignItems: "center", gap: 10, fontWeight: 800, color: "#e7ecff", textDecoration: "none" },
  brandMark: { width: 36, height: 36, borderRadius: 10, background: "linear-gradient(135deg,#ffc857,#ffb627)", color: "#0a0f24", display: "grid", placeItems: "center", fontWeight: 800, boxShadow: "0 0 30px rgba(255,200,87,0.4)" },
  callBtn: { display: "inline-flex", alignItems: "center", gap: 8, padding: "10px 18px", background: "linear-gradient(135deg,#ffc857,#ffb627)", color: "#0a0f24", fontWeight: 700, borderRadius: 999, fontSize: 13, textDecoration: "none" },
  main: { maxWidth: 1100, margin: "0 auto", padding: "60px 24px", position: "relative", zIndex: 2 },
  backLink: { display: "inline-flex", alignItems: "center", gap: 8, color: "#8b93b3", fontSize: 14, marginBottom: 24, textDecoration: "none" },
  tag: { display: "inline-block", padding: "6px 14px", borderRadius: 999, background: "rgba(111,139,255,0.1)", color: "#6f8bff", fontSize: 12, fontWeight: 600, letterSpacing: "0.1em", marginBottom: 16, border: "1px solid rgba(111,139,255,0.2)" },
  h1: { fontFamily: "'Sora', sans-serif", fontSize: "clamp(36px,6vw,68px)", fontWeight: 800, lineHeight: 1.1, marginBottom: 16, letterSpacing: "-0.02em" },
  gold: { background: "linear-gradient(135deg,#ffc857,#ff8a00)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text" },
  lead: { color: "#8b93b3", fontSize: 18, maxWidth: 720, marginBottom: 50, lineHeight: 1.6 },
  loading: { color: "#8b93b3", padding: "60px 0", textAlign: "center" },
  section: { marginBottom: 16 },
  sectionBtn: { width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between", padding: "20px 24px", borderRadius: 18, background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)", cursor: "pointer", color: "inherit", fontFamily: "inherit", transition: "all 0.3s" },
  sectionTitle: { display: "flex", alignItems: "center", gap: 16, fontFamily: "'Sora', sans-serif", fontWeight: 700, fontSize: 17, textAlign: "left" },
  sectionTag: { width: 40, height: 40, borderRadius: 10, display: "grid", placeItems: "center", border: "1px solid", fontSize: 18 },
  count: { fontSize: 12, color: "#8b93b3", fontWeight: 500, padding: "2px 10px", borderRadius: 999, background: "rgba(255,255,255,0.06)" },
  itemsGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))", gap: 16, padding: "20px 0" },
  itemCard: { display: "block", padding: 20, borderRadius: 14, background: "linear-gradient(180deg, rgba(255,255,255,0.04) 0%, rgba(255,255,255,0.01) 100%)", border: "1px solid", textDecoration: "none", color: "inherit", transition: "transform 0.3s, border-color 0.3s" },
  itemHead: { display: "flex", alignItems: "center", gap: 12, marginBottom: 10 },
  itemIcon: { width: 36, height: 36, borderRadius: 10, background: "rgba(255,255,255,0.04)", display: "grid", placeItems: "center", flexShrink: 0 },
  itemName: { fontWeight: 700, fontSize: 15, flex: 1, fontFamily: "'Sora', sans-serif" },
  itemDesc: { fontSize: 13, color: "#8b93b3", lineHeight: 1.55, margin: 0 },
  cta: { marginTop: 60, padding: "60px 32px", borderRadius: 28, background: "linear-gradient(135deg, rgba(255,200,87,0.08), rgba(111,139,255,0.08))", border: "1px solid rgba(255,200,87,0.2)", textAlign: "center" },
  ctaTitle: { fontFamily: "'Sora', sans-serif", fontWeight: 800, fontSize: "clamp(26px,4vw,40px)", marginBottom: 12 },
  ctaText: { color: "#8b93b3", fontSize: 16, maxWidth: 540, margin: "0 auto 28px" },
  ctaBtns: { display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap" },
  btnPrimary: { display: "inline-flex", alignItems: "center", gap: 8, padding: "16px 28px", background: "linear-gradient(135deg,#ffc857,#ffb627)", color: "#0a0f24", fontWeight: 700, borderRadius: 14, fontSize: 15, textDecoration: "none", boxShadow: "0 10px 40px rgba(255,200,87,0.25)" },
  btnSecondary: { display: "inline-flex", alignItems: "center", gap: 8, padding: "16px 28px", background: "rgba(255,255,255,0.06)", color: "#e7ecff", fontWeight: 600, borderRadius: 14, fontSize: 15, textDecoration: "none", border: "1px solid rgba(255,255,255,0.12)" },
  footer: { textAlign: "center", padding: "40px 24px", color: "#8b93b3", fontSize: 13, borderTop: "1px solid rgba(255,255,255,0.08)", marginTop: 60, position: "relative", zIndex: 2 },
};
